import {
    SharingLinkKind, AccessStatus, ISharingInformation,
    SharingCapabilityDisabledReason
} from '../interfaces/SharingInterfaces';
import { IPerson, EntityType } from '@ms/odsp-datasources/lib/PeoplePicker';
import * as StringHelper from '@ms/odsp-utilities/lib/string/StringHelper';
import * as React from 'react';
import { Link } from 'office-ui-fabric-react/lib/Link';
import stringsResx from '../components/Share.resx';
import PrincipalType from '@ms/odsp-datasources/lib/dataSources/roleAssignments/PrincipalType';
import shareStrings from '../components/Share.resx';

export function getExternalPeopleCount(selectedItems: Array<IPerson>): number {
    if (!selectedItems || selectedItems.length === 0) {
        return 0;
    }

    const externalPeople = selectedItems.filter((item: IPerson) => {
        return item.entityType === EntityType.externalUser;
    });

    return externalPeople.length;
}

export function getLinkedInPeopleCount(selectedItems: Array<IPerson>): number {
    if (!selectedItems || selectedItems.length === 0) {
        return 0;
    }

    const externalPeople = selectedItems.filter((item: IPerson) => {
        return item.entityType === EntityType.linkedinUser;
    });

    return externalPeople.length;
}

export function getOversharingExternalsWarning(selectedItems: Array<IPerson>): string {
    const externalPeople = selectedItems.filter((item: IPerson) => {
        return item.entityType === EntityType.externalUser;
    });
    const numberOfPeople = externalPeople.length;
    const names: Array<string> = externalPeople.map((persona: IPerson) => {
        return persona.name;
    });

    if (numberOfPeople === 2) {
        const result = names.join(', ');
        const oxfordComma: number = result.lastIndexOf(',');
        return `${result.substring(0, oxfordComma)} ${stringsResx.and} ${result.substring(oxfordComma + 1)} ${stringsResx.outsideOfYourOrgPlural}`;
    } else if (numberOfPeople > 2) {
        const result = names.join(', ');
        const oxfordComma: number = result.lastIndexOf(',');
        return `${result.substring(0, oxfordComma + 1)} ${stringsResx.and} ${result.substring(oxfordComma + 1)} ${stringsResx.outsideOfYourOrgPlural}`;
    } else if (numberOfPeople === 1) {
        return `${names[0]} ${stringsResx.outsideOfYourOrgSingular}`;
    } else {
        return '';
    }
}

export function getOversharingGroupsWarning(selectedItems: Array<IPerson>, groupsMemberCount: number): string {
    const groups = selectedItems.filter((item: IPerson) => {
        return item.entityType === EntityType.group;
    });
    const numberOfGroups = groups.length;

    let warningText = '';
    if (numberOfGroups === 1) {
        warningText = stringsResx.oneGroupInvited;
    } else if (numberOfGroups > 1) {
        warningText = StringHelper.format(stringsResx.multipleGroupsInvited, numberOfGroups);
    }

    if (groupsMemberCount >= 1000) {
        warningText += ` ${stringsResx.groupsMemberCountLargeLabel}`;
    } else if (groupsMemberCount >= 25) {
        warningText += ` ${StringHelper.format(stringsResx.groupsMemberCountLabel, groupsMemberCount)}`;
    }

    return warningText;
}

export interface IPickerErrorProps {
    selectedItems: Array<IPerson>;
    sharingLinkKind: SharingLinkKind;
    canAddExternalPrincipal: boolean;
    sharingInformation: ISharingInformation;
    viewPolicyTipCallback: () => void;
    permissionsMap?: { [index: string]: AccessStatus };
}

export function renderPickerError(props: IPickerErrorProps): JSX.Element {
    const {
        selectedItems,
        sharingLinkKind,
        canAddExternalPrincipal,
        sharingInformation,
        viewPolicyTipCallback,
        permissionsMap
    } = props;

    const policyTipError = (
        <div>
            <span>{ stringsResx.ptErrorMessage } </span>
            <Link onClick={ viewPolicyTipCallback }>
                { stringsResx.ptViewPolicyTipLabel }
            </Link>
        </div>
    );

    const hasDlpPolicyTip = sharingInformation.item.hasDlpPolicy || (sharingInformation.sharingAbilities && sharingInformation.sharingAbilities.directSharingAbilities.canAddExternalPrincipal.disabledReason === SharingCapabilityDisabledReason.documentIsProtectedByDlp);

    for (const selectedItem of selectedItems) {
        /**
         * Check for unresolved users, and use server message if we find one.
         *
         * In the case where we have an unresolved user and the item blocks
         * sharing via policy, use a special error message instead of the one
         * provided by the server.
         *
         * It shouldn't happen, but if the user doesn't get resolved, and the
         * server doesn't provide an error message, fall back to generic "no
         * exact match" error to at least provide some error message.
         */
        if (!selectedItem.isResolved) {
            if (hasDlpPolicyTip && !canAddExternalPrincipal) {
                return policyTipError;
            } else if (selectedItem.rawData) {
                return getErrorFromRawData(selectedItem.rawData, sharingInformation.customizedExternalSharingServiceUrl);
            } else {
                return <span>{ stringsResx.noExactMatch }</span>;
            }
        } else if (selectedItem.isExternal && hasDlpPolicyTip && !canAddExternalPrincipal) {
            return policyTipError;
        }

        if (selectedItem.isExternal && selectedItem.principalType === PrincipalType.distributionList && selectedItem.entityType === EntityType.group) {
            return <span>{ shareStrings.peoplePickerErrorDLNotSupported }</span>;
        }

        /**
         * If the user is external and the user has a CSL configured, show
         * specific error.
         */
        const isCompanyShareableLink = sharingLinkKind === SharingLinkKind.organizationEdit || sharingLinkKind === SharingLinkKind.organizationView;
        if (selectedItem.isExternal && isCompanyShareableLink) {
            return <span>{ stringsResx.peoplePickerErrorCsl }</span>;
        }
    }

    /**
     * If we have a permissionsMap and a canonical is being shared, we need to
     * check if desired recipients actually have permission to access the item.
     */
    if (permissionsMap && sharingLinkKind === SharingLinkKind.direct) {
        let usersWithoutPermissions = 0;
        for (const key in permissionsMap) {
            if (permissionsMap[key] === AccessStatus.none) {
                usersWithoutPermissions++;
            }
        }

        if (usersWithoutPermissions === 1) {
            return <span>{ stringsResx.insufficientPermissionsError }</span>;
        } else if (usersWithoutPermissions > 1) {
            return <span>{ stringsResx.insufficientPermissionsErrorPlural }</span>;
        }
    }

    return null;
}

function getErrorFromRawData(data: { Description: string, EntityData: { BlockingSharingPolicy: number } },
    customUrl: string): JSX.Element {
    const code = (data.EntityData && data.EntityData.BlockingSharingPolicy) || 0;

    if (code === 0) {
        return <span>{ data.Description }</span>;
    } else if (code >= 401 && code <= 404) {
        if (customUrl) {
            return <div>{
                StringHelper.formatToArray(stringsResx.externalSharingAllowDenyListWithLink,
                    code,
                    (
                        <a href={ customUrl } target='_blank'>
                            { stringsResx.noExternalSharingLearnMoreHere }
                        </a>
                    ))
            }</div>;
        } else {
            return <span>{ StringHelper.format(stringsResx.externalSharingAllowDenyList, code) }</span>;
        }
    } else if ((code >= 200 && code <= 209) || (code >= 301 && code <= 305)) {
        if (customUrl) {
            return <div>{
                StringHelper.formatToArray(stringsResx.externalSharingDisabledWithLink,
                    code,
                    (
                        <a href={ customUrl } target='_blank'>
                            { stringsResx.noExternalSharingLearnMoreHere }
                        </a>
                    ))
            }</div>;
        } else {
            return <span>{ StringHelper.format(stringsResx.externalSharingDisabled, code) }</span>;
        }
    }
}
